
import { useNavigate } from "react-router-dom";
import { useCallback, useEffect } from "react";
import { cn } from "@/lib/utils";
import { DrawingHistorySheet } from "@/components/drawing-history/DrawingHistorySheet";
import { useAuth } from "@/components/AuthProvider";
import { ProfileSheet } from "@/components/profile/ProfileSheet";
import { Feedback } from "@/components/feedback/Feedback";
import { MenuDropdown } from "./menu/MenuDropdown";
import { StudioInfo } from "./menu/StudioInfo";
import { useMenuState } from "./menu/useMenuState";
import { CustomCanvas } from "@/types/fabric";

interface StudioMenuProps {
  onPricingClick: () => void;
  onLoadDrawing: (drawingData: string, drawingId: string) => void;
  setIsToolbarVisible?: (visible: boolean) => void;
  currentTitle?: string;
  onClear: () => void;
  canvas?: CustomCanvas | null; // Add canvas prop to the interface
}

export const StudioMenu = ({ 
  onPricingClick, 
  onLoadDrawing, 
  setIsToolbarVisible, 
  currentTitle, 
  onClear,
  canvas // Add canvas to the destructured props
}: StudioMenuProps) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { 
    showHistory, 
    showProfile, 
    showFeedback, 
    handleMenuItemOpen, 
    handleMenuItemClose 
  } = useMenuState({ setIsToolbarVisible });

  useEffect(() => {
    return () => {
      document.body.style.pointerEvents = 'auto';
    };
  }, []);

  const handleDrawingLoad = useCallback((drawingData: string, drawingId: string) => {
    console.log('StudioMenu: Handling drawing load, passing to parent');
    onLoadDrawing(drawingData, drawingId);
    handleMenuItemClose('history');
  }, [onLoadDrawing, handleMenuItemClose]);

  const handleProfileClose = useCallback((isOpen: boolean) => {
    if (!isOpen) {
      handleMenuItemClose('profile');
    }
  }, [handleMenuItemClose]);

  const handleFeedbackClose = useCallback((isOpen: boolean) => {
    if (!isOpen) {
      handleMenuItemClose('feedback');
    }
  }, [handleMenuItemClose]);

  const handleHistoryOpen = useCallback(() => {
    // Only open if authenticated
    if (user) {
      handleMenuItemOpen('history');
    }
  }, [user, handleMenuItemOpen]);

  const handleProfileOpen = useCallback(() => {
    // Only open if authenticated
    if (user) {
      handleMenuItemOpen('profile');
    }
  }, [user, handleMenuItemOpen]);

  return (
    <>
      <div className={cn(
        "fixed left-0 z-40 p-4",
        user ? "top-3" : "top-12"
      )}>
        <div className="flex items-center gap-3 backdrop-blur-xl bg-white/90 rounded-xl border border-white/60 shadow-lg px-6 py-2.5 transition-all duration-300 hover:shadow-xl">
          <div className="relative flex items-center after:content-[''] after:absolute after:-bottom-1 after:left-0 after:right-0 after:h-[1px] after:bg-gradient-to-r after:from-purple-200/0 after:via-purple-200/50 after:to-purple-200/0">
            <MenuDropdown
              onNewSession={onClear}
              onHistoryOpen={handleHistoryOpen}
              onProfileOpen={handleProfileOpen}
              onFeedbackOpen={() => handleMenuItemOpen('feedback')}
              onPricingClick={onPricingClick}
            />
          </div>
          
          <div className="h-4 w-px bg-gradient-to-b from-gray-100 via-gray-200 to-gray-100" />
          
          <StudioInfo
            onPricingClick={onPricingClick}
            currentTitle={currentTitle}
          />
        </div>
      </div>
      
      <DrawingHistorySheet 
        open={showHistory}
        onOpenChange={() => handleMenuItemClose('history')}
        onLoadDrawing={handleDrawingLoad}
        onRefreshNeeded={(refreshFn) => {
          console.log("Refresh needed");
        }}
        canvas={canvas} // Pass the canvas prop to DrawingHistorySheet
      />

      <ProfileSheet 
        open={showProfile}
        onOpenChange={handleProfileClose}
      />

      <Feedback
        showPricing={false}
        open={showFeedback}
        onOpenChange={handleFeedbackClose}
      />
    </>
  );
};
